import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTenant } from "../context/TenantContext";
import TurfCanvas from "../components/TurfCanvas";
import { calcPrice, createCheckout } from "../lib/api";
import { OfferPrice, IncludedList, AllInNote, YouSave, money as fmtCcy } from "../components/PriceOffer";
import CurrencySwitcher from "../components/CurrencySwitcher";
import { useCurrency } from "../context/CurrencyContext";
import AddressFinder from "../components/AddressFinder";

const money = (v) => "£" + Number(v || 0).toFixed(2);
const REQUIRED = ["firstName", "lastName", "email", "phone", "address1", "city", "postcode"];

export default function OrderReview() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [production, setProduction] = useState("standard");
  const [price, setPrice] = useState(null);
  const [form, setForm] = useState({ country: "United Kingdom" });
  const [checks, setChecks] = useState({ dims: false, preview: false });
  const [busy, setBusy] = useState(false);
  const { currency } = useCurrency();

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem("pgt_review") || localStorage.getItem("pgt_design_v1"));
      const d = s?.design || s;
      if (!d || !d.dimensions) { navigate("/design"); return; }
      d.objects = Array.isArray(d.objects) ? d.objects : [];
      setDesign(d);
    } catch (e) { navigate("/design"); }
  }, [navigate]);

  useEffect(() => {
    if (!design) return;
    calcPrice({
      length: design.dimensions.length, width: design.dimensions.width,
      logos: design.objects.filter((o) => o.type === "logo"),
      graphics: design.objects.filter((o) => o.type === "graphic"),
      texts: design.objects.filter((o) => o.type === "text"),
      production_speed: production, complex_artwork: design.complex_artwork,
      postcode: form.postcode || "", country: form.country || "", currency,
    }).then(setPrice).catch(() => {});
  }, [design, production, form.postcode, form.country, currency]);

  const prod = tenant?.production || {};
  const tr = tenant?.turnaround || {};
  const expAllowed = (tr.express_enabled !== false) && (tr.express_max_width == null || (design?.dimensions?.width || 0) <= tr.express_max_width);
  useEffect(() => { if (production === "express" && !expAllowed) setProduction("standard"); }, [expAllowed, production]);
  const logos = useMemo(() => (design?.objects || []).filter((o) => o.type === "logo"), [design]);
  const gfx = useMemo(() => (design?.objects || []).filter((o) => o.type === "graphic"), [design]);
  const texts = useMemo(() => (design?.objects || []).filter((o) => o.type === "text"), [design]);

  if (!design) return <div className="min-h-screen bg-pgt-bg" />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = REQUIRED.every((k) => (form[k] || "").trim()) && checks.dims && checks.preview;
  const LABELS = { firstName: "first name", lastName: "last name", email: "email", phone: "phone", address1: "house number & street", city: "town/city", postcode: "postcode" };
  const missing = [...REQUIRED.filter((k) => !(form[k] || "").trim()).map((k) => LABELS[k]), ...(!checks.dims ? ["dimensions confirmation"] : []), ...(!checks.preview ? ["preview confirmation"] : [])];

  const pay = async () => {
    if (!valid) { toast.error("Please complete all required fields and confirmations."); return; }
    setBusy(true);
    try {
      const r = await createCheckout({
        design, production_speed: production, currency,
        delivery: form, contact: { name: `${form.firstName} ${form.lastName}`, email: form.email, phone: form.phone },
      });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-pgt-bg">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="pgt-eyebrow mb-2">Order Review</div>
        <h1 className="pgt-heading text-white text-4xl sm:text-5xl mb-6">Check Your Design</h1>
        <div className="grid lg:grid-cols-[1fr_420px] gap-6">
          <div className="space-y-6">
            <div className="border border-white/10 bg-[#111] rounded-sm h-[360px]" data-testid="review-preview">
              <TurfCanvas design={design} readOnly showSafe={false} />
            </div>

            <div className="border border-white/10 bg-pgt-surface rounded-sm p-5">
              <h3 className="font-display font-bold uppercase tracking-wider text-white mb-3">Production Speed</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[["standard", prod.standard_label, tr.standard_turnaround || prod.standard_time], ["express", prod.express_label, tr.express_turnaround || prod.express_time]]
                  .filter(([v]) => (v === "standard" ? (tr.standard_enabled !== false) : ((tr.express_enabled !== false) && expAllowed)))
                  .map(([v, label, time]) => (
                  <button key={v} onClick={() => setProduction(v)} data-testid={`production-${v}`}
                    className={`text-left p-4 rounded-sm border transition-colors ${production === v ? "border-pgt-green bg-pgt-green/5" : "border-white/15 hover:border-white/30"}`}>
                    <div className="font-display font-bold uppercase text-white">{label}</div>
                    <div className="text-white/60 text-sm mt-1">{time}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-white/40 mt-3">{prod.note}</p>
            </div>

            <div className="border border-white/10 bg-pgt-surface rounded-sm p-5">
              <h3 className="font-display font-bold uppercase tracking-wider text-white mb-4">Delivery Details</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[["firstName", "First name"], ["lastName", "Last name"], ["email", "Email"], ["phone", "Phone"]].map(([k, label]) => (
                  <div key={k}>
                    <label className="text-xs uppercase tracking-wider text-white/50">{label} *</label>
                    <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`delivery-${k}`}
                      className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <AddressFinder form={form} set={set} />
              </div>
            </div>
          </div>

          {/* summary */}
          <div className="space-y-4">
            <div className="border border-white/10 bg-pgt-surface rounded-sm p-5 lg:sticky lg:top-20">
              <div className="flex items-center justify-between mb-3">
                <div className="pgt-eyebrow">Summary</div>
                <CurrencySwitcher />
              </div>
              <div className="space-y-1 text-sm text-white/70">
                <Row l="Dimensions" v={`${design.dimensions.length}m × ${design.dimensions.width}m`} />
                <Row l="Area" v={`${(design.dimensions.length * design.dimensions.width).toFixed(2)} m²`} />
                <Row l="Turf colour" v={design.turf_colour_name} />
                <Row l="Marking colour" v={design.options.marking_colour} />
                <Row l="Logo(s)" v={logos.map((l) => l.filename).join(", ") || "None"} />
                <Row l="Graphics" v={gfx.length || "None"} />
                <Row l="Custom text" v={texts.map((t) => t.text).join(", ") || "None"} />
                <Row l="Production" v={production === "express" ? prod.express_label : prod.standard_label} />
              </div>
              {price && (
                <div className="border-t border-white/10 mt-3 pt-4 space-y-3">
                  <OfferPrice rrp={price.rrp} price={price.sale_price} discount={price.discount_pct} currency={price.currency} />
                  <YouSave amount={price.you_save} currency={price.currency} />
                  <AllInNote note={price.all_in_note} offerLabel={price.offer_active ? price.offer_label : null} />
                  <div className="text-white/50 text-xs" data-testid="review-delivery-time">{production === "express" ? price.options.express.delivery_time : price.options.standard.delivery_time}</div>
                  {price.delivery_surcharge > 0 && <Row l="Delivery surcharge" v={fmtCcy(price.delivery_surcharge, price.currency)} muted />}
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="font-display font-bold uppercase text-white">Total</span>
                    <span className="font-display font-extrabold text-2xl text-pgt-green" data-testid="review-total">{fmtCcy(price.total, price.currency)}</span>
                  </div>
                  <IncludedList items={price.included} />
                  {price.checkout_terms && <p className="text-[11px] text-white/45 leading-relaxed border-t border-white/10 pt-3" data-testid="checkout-terms">{price.checkout_terms}</p>}
                </div>
              )}
              <label className="flex items-start gap-2 mt-4 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" checked={checks.dims} onChange={(e) => setChecks((c) => ({ ...c, dims: e.target.checked }))} data-testid="check-dims" className="mt-0.5 accent-[#00D26A]" />
                <span>I have checked my dimensions and understand that this product will be custom manufactured to my specification.</span>
              </label>
              <label className="flex items-start gap-2 mt-2 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" checked={checks.preview} onChange={(e) => setChecks((c) => ({ ...c, preview: e.target.checked }))} data-testid="check-preview" className="mt-0.5 accent-[#00D26A]" />
                <span>I understand the on-screen preview is a visual representation and colours may vary slightly.</span>
              </label>
              <button onClick={pay} disabled={!valid || busy} data-testid="pay-btn"
                className="w-full mt-4 bg-pgt-green text-black font-display font-bold uppercase tracking-wider py-3.5 rounded-sm hover:bg-pgt-greenHover transition-colors disabled:opacity-40 pgt-glow">
                {busy ? "Redirecting…" : "Pay Securely →"}
              </button>
              {!valid && !busy && missing.length > 0 && (
                <p className="text-[11px] text-amber-300/90 mt-2 text-center" data-testid="pay-missing-hint">Still needed: {missing.join(", ")}.</p>
              )}
              <p className="text-[11px] text-white/40 mt-2 text-center">Secure payment via Stripe · Card details never stored by us.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ l, v, muted }) => (
  <div className="flex justify-between gap-3"><span className={muted ? "text-white/50" : "text-white/60"}>{l}</span><span className="text-white text-right">{v}</span></div>
);
